type ExtensionFn = (...args: unknown[]) => unknown;

/**
 * The factory classes all inherit from the BaseFactory. The factories aren't
 * much more than a namespace for a bunch of extensions.
 */
export default class BaseFactory {

    protected extensions: Map<unknown, ExtensionFn>;
    private static instance: unknown;

    constructor() {
        this.extensions = new Map();
    }

    /**
     * Factories are often useful as singletons.
     */
    static getInstance() {
        if(! this.instance){
            this.instance = new this();
        }
        return this.instance;
    }

    /**
     * Subclasses can override the make method and delegate the extension call
     * to super.make() and then do any required caching or post processing.
     */
    make(name: string, ...args: unknown[]) {
        var ext = this.extensions.get(name);
        if(ext == null){
            throw new Error(`This factory doesn't have an extension by the name "${name}"`);
        }
        return ext.apply(this, args);
    }

    /**
     * Add a factory extension. The supplied function must be a full
     * fledged function, not a fat arrow function. The extension will be called
     * bound to the mesh factory instance so it can access the cache.
     */
    extend(name: string, f: ExtensionFn) {
        if(this.extensions.has(name)) {
            throw new Error(`The extend method cannot override the extension named "${name}". Use the override method if you really want to override the existing extension.`);
        }
        this.extensions.set(name, f);
    }

    /**
     * The extend method won't let you add two extensions by the same name, but
     * you can override an extension with this method. The old extension is
     * returned in case you plan to restore it later.
     */
    override(name: string, f: ExtensionFn) {
        var old = this.extensions.get(name);
        this.extensions.set(name, f);
        return old;
    }
}
